import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, BaseSelect, Loader } from 'ui-kit';
import { BUTTON_TYPES } from '_constants';
import useDetachVisitFromCampaign from 'hooks/useDetachVisitFromCampaign';
import * as Styled from '../styles';
import * as Local from './styles';

// `linked_campaigns` entries carry `is_primary`: the primary one is the visit's own
// campaign/assignment FK, the rest are secondary VisitCampaignLink rows. The detach
// hook needs that distinction to pick the right endpoint.
const buildOption = ({ id, campaign_name: campaignName, agency_name: agencyName, is_primary: isPrimary }) => ({
  value: id,
  label: agencyName ? `${campaignName} (agency ${agencyName})` : campaignName,
  isLinked: !isPrimary,
});

function DetachFromCampaignModal({ isOpen, onClose, visitId, linkedCampaigns, updateVisitInfo }) {
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isValid, setIsValid] = useState(true);
  const { detachVisit, detachVisitFromAll, isDetaching } = useDetachVisitFromCampaign();

  const options = useMemo(() => linkedCampaigns.map(buildOption), [linkedCampaigns]);
  // With a single campaign there is nothing to pick — preselect it so the user only confirms.
  const value = selectedCampaign || (options.length === 1 ? options[0] : null);

  if (!isOpen) return null;

  const handleCloseModal = () => {
    setSelectedCampaign(null);
    setIsValid(true);
    onClose();
  };

  const handleChangeSelect = (option) => {
    setSelectedCampaign(option);
    setIsValid(true);
  };

  const handleProceedDetach = async () => {
    if (!value?.value) {
      setIsValid(false);
      return;
    }
    const isDetached = await detachVisit({ visitId, campaignId: value.value, isLinked: value.isLinked });
    if (!isDetached) return;
    handleCloseModal();
    updateVisitInfo();
  };

  const handleDetachFromAll = async () => {
    const { detached, failed } = await detachVisitFromAll({
      visitId,
      campaigns: options.map(({ value: campaignId, isLinked }) => ({ id: campaignId, isLinked })),
    });
    // A partial detach still changed the visit, so refresh either way and keep the
    // modal open when something failed so the user can retry what is left.
    if (detached) updateVisitInfo();
    if (!failed) handleCloseModal();
  };

  if (isDetaching) return <Loader />;

  return (
    <Modal open={isOpen} onClose={handleCloseModal} preventEscape preventDesktopOverflow>
      <Local.ModalWrapper>
        <Styled.ModalTitle>Detach from Campaign</Styled.ModalTitle>
        <Styled.ModalDescription>
          Choose the campaign you want to un-attach this visit from
          {options.length > 1 && ', or use "Detach from all" to un-attach it from every campaign at once'}. The visit
          itself will not be deleted.
        </Styled.ModalDescription>
        <Styled.SelectWrapper>
          <BaseSelect
            inputId="selectDetachCampaignModalDrop"
            onChange={handleChangeSelect}
            name="Select Campaign"
            options={options}
            value={value}
            menuPlacement="top"
            error={!isValid && 'Select a campaign'}
            className={!isValid && 'error'}
          />
        </Styled.SelectWrapper>
        <Local.ModalButtonWrapper>
          <Styled.ModalButtonCancel
            onClick={handleCloseModal}
            text="Cancel"
            variant={BUTTON_TYPES.DARK}
            aria-label="Cancel"
          />
          <Styled.ModalButtonSet
            onClick={handleProceedDetach}
            text="Detach"
            variant={BUTTON_TYPES.DANGER}
            aria-label="Detach"
            disabled={isDetaching}
          />
          <Styled.ModalButtonSet
            onClick={handleDetachFromAll}
            text="Detach from all"
            variant={BUTTON_TYPES.DANGER}
            aria-label="Detach from all"
            disabled={isDetaching}
          />
        </Local.ModalButtonWrapper>
      </Local.ModalWrapper>
    </Modal>
  );
}

DetachFromCampaignModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  visitId: PropTypes.string,
  linkedCampaigns: PropTypes.arrayOf(PropTypes.object),
  updateVisitInfo: PropTypes.func,
};

DetachFromCampaignModal.defaultProps = {
  visitId: '',
  linkedCampaigns: [],
  updateVisitInfo: () => {},
};

export default DetachFromCampaignModal;
